import * as z from 'zod/mini';
import { CHECKLIST_TEMPLATE_KEYS } from '../checklists.ts';
import {
  CHECKLIST_ITEM_MAX_LENGTH,
  CHECKLIST_ITEM_NOTE_MAX_LENGTH,
  CHECKLIST_NAME_MAX_LENGTH,
} from '../constants.ts';
import { idSchema, pathSchema, timestampSchema } from './common.ts';
import { shownName } from './user.ts';

// A checklist is a named list of steps on a property — a commissioning
// procedure, a handover, the annual service — and the record of who ticked
// each step and when. Steps are ticked one at a time from a phone, so every
// write is small: one step's done flag, note, text, or position. The maker
// and each ticker are copied onto the rows by username and display name so
// the record still reads once the account is gone.

export const checklistTemplateKeySchema = z.enum(CHECKLIST_TEMPLATE_KEYS);

export const checklistNameSchema = z
  .string()
  .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(CHECKLIST_NAME_MAX_LENGTH));

export const checklistItemTextSchema = z
  .string()
  .check(z.trim(), z.minLength(1, 'Write the step first'), z.maxLength(CHECKLIST_ITEM_MAX_LENGTH));

export const checklistItemNoteSchema = z
  .string()
  .check(z.trim(), z.maxLength(CHECKLIST_ITEM_NOTE_MAX_LENGTH));

export const checklistSchema = z.object({
  id: idSchema,
  propertyId: idSchema,
  name: z.string(),
  /** The template key the steps were copied from; '' for a custom list. */
  template: z.string(),
  /** Who made the list; null once their account is removed. */
  userId: z.nullable(idSchema),
  username: z.string(),
  displayName: z.string(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  /** Set the moment the last step was ticked; null while any step is open. */
  completedAt: z.nullable(timestampSchema),
});
export type Checklist = z.infer<typeof checklistSchema>;

export const checklistItemSchema = z.object({
  id: idSchema,
  checklistId: idSchema,
  /** 0-based order within the list. */
  position: z.int(),
  text: z.string(),
  done: z.boolean(),
  /** Who ticked it; null while open, or once their account is removed. */
  doneBy: z.nullable(idSchema),
  doneByUsername: z.string(),
  doneByDisplayName: z.string(),
  doneAt: z.nullable(timestampSchema),
  /** What was found or done at the step; '' when nothing was written. */
  note: z.string(),
  createdAt: timestampSchema,
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

/** A list as the property page shows it: the row plus how far along it is. */
export const checklistSummarySchema = z.object({
  ...checklistSchema.shape,
  done: z.int(),
  total: z.int(),
});
export type ChecklistSummary = z.infer<typeof checklistSummarySchema>;

export const checklistListSchema = z.object({
  /** Oldest first, the order they were made in. */
  checklists: z.array(checklistSummarySchema),
});

/** The checklist page: the list, its steps in order, and the property's path for the breadcrumb. */
export const checklistDetailSchema = z.object({
  checklist: checklistSchema,
  items: z.array(checklistItemSchema),
  path: pathSchema,
});
export type ChecklistDetail = z.infer<typeof checklistDetailSchema>;

export const checklistEnvelopeSchema = z.object({ checklist: checklistSchema });
export const checklistItemEnvelopeSchema = z.object({ item: checklistItemSchema });

export const checklistInputSchema = z.strictObject({
  name: checklistNameSchema,
  template: checklistTemplateKeySchema,
});
export type ChecklistInput = z.infer<typeof checklistInputSchema>;

export const checklistPatchSchema = z.strictObject({ name: checklistNameSchema });
export type ChecklistPatch = z.infer<typeof checklistPatchSchema>;

export const checklistItemInputSchema = z.strictObject({ text: checklistItemTextSchema });
export type ChecklistItemInput = z.infer<typeof checklistItemInputSchema>;

export const checklistItemPatchSchema = z.strictObject({
  text: z.optional(checklistItemTextSchema),
  done: z.optional(z.boolean()),
  note: z.optional(checklistItemNoteSchema),
  /** Move the step to this 0-based place; past the end means last. */
  position: z.optional(z.int().check(z.gte(0))),
});
export type ChecklistItemPatch = z.infer<typeof checklistItemPatchSchema>;

/** "7 of 10 done", or that there is nothing to tick yet. */
export function checklistProgress(list: Pick<ChecklistSummary, 'done' | 'total'>): string {
  return list.total === 0 ? 'No steps yet' : `${String(list.done)} of ${String(list.total)} done`;
}

/** The name of whoever ticked a step (display name, else username); '' while it is open. */
export function doneByName(
  item: Pick<ChecklistItem, 'doneByUsername' | 'doneByDisplayName'>,
): string {
  return shownName({ username: item.doneByUsername, displayName: item.doneByDisplayName });
}
